import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { Frame, VisualEditFrameEntry } from 'dragonmouse-shared/interfaces';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectActiveFrameChat } from 'redux/db/reducers';
import { canEditFrame } from 'redux/db/util';
import { displayViewOnlyModal } from 'redux/mode/util';
import * as S from './styles';

interface VisualEditEntryProps {
	entry: VisualEditFrameEntry;
	frame: Frame;
	frameId: string;
	isActive: boolean;
}

const VisualEditEntry = ({ entry, frame, frameId, isActive }: VisualEditEntryProps) => {
	const [isHovered, setIsHovered] = useState(false);
	const frameChat = useSelector(selectActiveFrameChat);
	const length = entry.visualEdits.length;
	const subLabel = length > 0 ? `${length} visual edits` : 'visual edit in progress';

	const versionLabel = (() => {
		const chat = frameChat?.frameId === frameId ? frameChat : null;
		const siteChat = chat?.entries.find((e) => e.kind === 'site' && e.siteId === entry.siteId);
		if (siteChat && siteChat.kind === 'site') return siteChat.label;
		return 'Prior Version';
	})();

	const onClick = () => {
		if (!canEditFrame(frame)) {
			displayViewOnlyModal();
			return;
		}

		// Set frame.siteId to this visual edit's siteId and enable visual edit
		forge.updateFrame(
			{
				update: {
					id: frameId,
					siteId: entry.siteId,
					metadata: {
						...frame.metadata,
						visualEditActive: true,
						veHash: entry.hash || null,
					},
				},
			},
			true
		);
	};

	// Convention: Visual edit entries should be deleted when they become empty.
	// This matches the behavior in visualEdits.removeEditFromFrame which also
	// deletes the entry when the last visual edit is removed.
	const onDelete = (e: React.MouseEvent) => {
		e.stopPropagation();

		if (!canEditFrame(frame)) {
			displayViewOnlyModal();
			return;
		}

		forge.deleteFrameEntry(
			{
				frameId,
				entryId: entry.id,
			},
			true
		);

		// If this was the active visual edit, also update metadata to disable visual edit mode
		if (frame.metadata.visualEditActive && frame.siteId === entry.siteId) {
			forge.updateFrame(
				{
					update: {
						id: frameId,
						metadata: {
							...frame.metadata,
							visualEditActive: false,
							veHash: null,
						},
					},
				},
				true
			);
		}
	};

	return (
		<S.Base
			active={isActive}
			onClick={onClick}
			disabled={false}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<S.LabelHeader>
				<S.Label>{versionLabel} Visual Edits</S.Label>
				<S.Spacer />
				<S.TrashIcon isHovered={isHovered} onClick={onDelete}>
					<Icon type={IconType.TRASH} width={16} height={16} />
				</S.TrashIcon>
				<S.Icon>
					<Icon
						type={isActive ? IconType.CHECK : IconType.LINE_ARROW_RIGHT}
						width={16}
						height={16}
					/>
				</S.Icon>
			</S.LabelHeader>
			<S.LabelDetails>
				<S.SubLabel>{subLabel}</S.SubLabel>
			</S.LabelDetails>
		</S.Base>
	);
};

export default VisualEditEntry;
